import { __rest } from "tslib";
import { jsx as _jsx } from "react/jsx-runtime";
import { addErrorNotification, deepMerge, logger, NotificationType } from '@vista-digital/occ-domain';
import { ErrorBoundary } from '@vista-digital/react-tools';
import { isObject } from '@vista-digital/validator';
import { observer } from 'mobx-react';
import { forwardRef, useCallback, useRef } from 'react';
import { useOccContext } from '../context/occ/hooks/use-occ-context';
import { Message } from '../subcomponents/message/message';
import { isPropProvider } from '../types/prop-provider';
import { getIdForComponentNotification } from './get-id-for-component-notification';
export function withComponentWrapper(Component, defaultPropsP2, propValidatorP2) {
    // We have to use a second generic type P2 to prevent P being inferred as ComponentProps without the ref.
    const defaultProps = defaultPropsP2;
    const propValidator = propValidatorP2;
    const displayName = `Vista${Component.displayName}Component`; // e.g. VistaSeatPickerComponent
    const VistaComponent = observer(props => {
        const propCacheRef = useRef(new Map());
        const propCache = propCacheRef.current;
        // This is used to ensure that if the same prop is passed in then we return a cached merge.
        // Without this all props will be recreated even if only one prop is changed. This causes
        // issues when attempting to memoise based on the props.
        // TODO: Investigate other solutions, this was added primarily for the SeatPicker. #286501
        const mergePropsWithCache = useCallback((providedProps) => {
            const defaultPropsValue = isPropProvider(defaultProps) ? defaultProps(providedProps) : defaultProps;
            const newPropCache = new Map();
            const mergedProps = Object.keys(defaultPropsValue).reduce((acc, key) => {
                if (!(key in providedProps)) {
                    acc[key] = defaultPropsValue[key];
                }
                else if (propCache.has(providedProps[key])) {
                    acc[key] = propCache.get(providedProps[key]);
                    newPropCache.set(providedProps[key], acc[key]);
                }
                else if (isObject(defaultPropsValue[key]) && isObject(providedProps[key])) {
                    acc[key] = deepMerge(defaultPropsValue[key], providedProps[key]);
                    newPropCache.set(providedProps[key], acc[key]);
                }
                return acc;
            }, Object.assign({}, providedProps));
            propCacheRef.current = newPropCache;
            return mergedProps;
        }, [propCache]);
        const { forwardedRef } = props, providedProps = __rest(props, ["forwardedRef"]);
        const mergedProps = mergePropsWithCache(providedProps);
        const valid = propValidator(mergedProps, logger.warn, displayName);
        if (!valid) {
            logger.warn(`Some properties for ${displayName} are invalid. See the log for more details.`);
        }
        return _jsx(Component, Object.assign({}, mergedProps, { ref: forwardedRef }));
    });
    const forwardRefComponent = forwardRef((props, ref) => {
        const { assets: occAssets, displayText: occDisplayText } = useOccContext();
        const handleOnError = (error, _info) => {
            addErrorNotification({
                id: getIdForComponentNotification('ErrorBoundary', NotificationType.Error, 'unexpected-error'),
                messages: [occDisplayText.componentErrorTitle]
            }, error);
        };
        return (_jsx(ErrorBoundary, { fallback: _jsx(Message, { iconName: occAssets.componentErrorIconName, title: occDisplayText.componentErrorTitle }), onError: handleOnError, children: _jsx(VistaComponent, Object.assign({}, props, { forwardedRef: ref })) }));
    });
    forwardRefComponent.displayName = displayName;
    return forwardRefComponent;
}
//# sourceMappingURL=with-component-wrapper.js.map